import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

export const METRICS = ["views", "reach", "likes", "comments", "shares", "saves"];
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

export function totals(t) {
  const out = Object.fromEntries(METRICS.map((k) => [k, 0]));
  Object.values(t.metrics || {}).forEach((v) => METRICS.forEach((k) => { out[k] += Number(v[k] || 0); }));
  out.engagement = out.likes + out.comments + out.shares + out.saves;
  return out;
}

export function MetricsEditor({ item, reload }) {
  const [m, setM] = useState({});
  useEffect(() => { setM(item.metrics || {}); }, [item]);
  const plats = item.platforms || [];
  if (!plats.length) return <div className="text-xs text-zinc-500">Add a platform to this post to log its performance.</div>;
  const set = (p, k, v) => setM((x) => ({ ...x, [p]: { ...(x[p] || {}), [k]: v.replace(/\D/g, "") } }));
  const save = async () => {
    try {
      await api.put(`/marketing/content/${item.id}/metrics`, { metrics: Object.fromEntries(plats.map((p) => [p, Object.fromEntries(METRICS.map((k) => [k, Number((m[p] || {})[k] || 0)]))])) });
      toast.success("Performance saved");
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="content-metrics" className="rounded-lg border border-zinc-200 p-3">
      <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Post performance{item.metrics_updated_at && <span className="font-normal normal-case tracking-normal text-zinc-400">Updated {new Date(item.metrics_updated_at).toLocaleDateString("en-IN")} by {item.metrics_updated_by}</span>}
      </div>
      <div className="thin-scroll overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-zinc-500"><th className="py-1 pr-2 text-left font-medium">Platform</th>{METRICS.map((k) => <th key={k} className="px-1 py-1 font-medium capitalize">{k}</th>)}</tr></thead>
          <tbody>
            {plats.map((p) => (
              <tr key={p}>
                <td className="py-1 pr-2 font-medium text-zinc-700">{p}</td>
                {METRICS.map((k) => (
                  <td key={k} className="px-1 py-1">
                    <input data-testid={`metric-${p.split(" ")[0].toLowerCase()}-${k}`} inputMode="numeric" value={(m[p] || {})[k] ?? ""} onChange={(e) => set(p, k, e.target.value)} placeholder="0"
                      className="h-8 w-20 rounded-md border border-zinc-200 px-2 text-right font-mono outline-none focus:border-[#A9792C]" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button data-testid="content-metrics-save" size="sm" className="mt-2" onClick={save}>Save performance</Button>
    </div>
  );
}

export function YouTubeSync({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/youtube/sync");
      toast.success(`YouTube stats updated for ${data.synced.length} post(s)${data.not_found.length ? ` · ${data.not_found.length} video(s) not found` : ""}`);
      onDone && (await onDone());
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
      <Button data-testid="youtube-sync-button" size="sm" variant="outline" disabled={busy} onClick={run}>{busy ? "Syncing…" : "Sync YouTube stats"}</Button>
      <span>Fills views, likes and comments for published posts with a YouTube video link. Runs automatically every morning too.</span>
    </div>
  );
}

export function Performance({ items, onOpen, onSynced }) {
  const pub = items.filter((t) => t.stage === "Published");
  const rows = [...pub].sort((a, b) => totals(b).engagement - totals(a).engagement || totals(b).views - totals(a).views);
  const sum = (k) => pub.reduce((s, t) => s + totals(t)[k], 0);
  const missing = pub.filter((t) => !t.metrics).length;
  return (
    <div className="space-y-3" data-testid="marketing-performance">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        {[...METRICS].map((k) => <div key={k} data-testid={`perf-total-${k}`} className="rounded-xl border border-zinc-200 bg-white p-3"><div className="text-[11px] uppercase tracking-wider text-zinc-400">{k}</div><div className="text-xl font-bold text-zinc-900">{fmt(sum(k))}</div></div>)}
      </div>
      {missing > 0 && <div data-testid="perf-missing" className="text-xs text-amber-700">{missing} published post(s) have no performance logged yet.</div>}
      <YouTubeSync onDone={onSynced} />
      <div className="thin-scroll overflow-x-auto rounded-xl border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs text-zinc-500"><tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Post</th>{METRICS.map((k) => <th key={k} className="px-3 py-2 capitalize">{k}</th>)}<th className="px-3 py-2">Engagement</th></tr></thead>
          <tbody>
            {rows.map((t, i) => {
              const s = totals(t);
              return (
                <tr key={t.id} data-testid={`perf-row-${t.id}`} onClick={() => onOpen(t)} className="cursor-pointer border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-3 py-2 text-zinc-400">{i + 1}</td>
                  <td className="px-3 py-2"><div className="font-medium text-zinc-900">{t.deliverable}</div><div className="text-[11px] text-zinc-500">{(t.platforms || []).join(", ")} · {(t.published_at || "").slice(0, 10)}</div></td>
                  {METRICS.map((k) => <td key={k} className="px-3 py-2 text-center font-mono text-xs">{t.metrics ? fmt(s[k]) : "—"}</td>)}
                  <td className="px-3 py-2 text-center font-mono text-xs font-bold text-[#A9792C]">{t.metrics ? fmt(s.engagement) : "—"}</td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-zinc-400">No published posts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
